/**
 * Scheletele de incarcare.
 *
 * Un schelet nu e decor: inlocuieste saltul din "ecran gol" in "meniu complet"
 * cu o forma stabila, care are DEJA dimensiunile continutului real. Cand
 * raspunsul soseste, textul se aseaza peste aceeasi geometrie si nimic nu se
 * misca — spre deosebire de un spinner centrat, dupa care pagina se reconstruie
 * brusc si lista sare sub deget.
 *
 * Doua reguli le tin oneste:
 *  - **Ascunse la cititoarele de ecran.** Sunt o promisiune vizuala, nu
 *    continut. Anuntul "se incarca" e treaba lui `StateScreen`, cu `aria-live`,
 *    o singura data — nu al fiecarui dreptunghi in parte.
 *  - **Aceeasi geometrie ca lucrul real.** Un schelet care nu seamana cu ce
 *    urmeaza produce exact saltul pe care trebuia sa il previna, plus asteptarea
 *    inselata.
 *
 * Pulsatia se opreste singura la `prefers-reduced-motion` (regula globala din
 * `index.css`), deci nu are nevoie de tratament aici.
 */

/** Un dreptunghi de text. `w` accepta orice latime Tailwind (`w-2/3`, `w-24`). */
export function SkeletonLine({ w = "w-full", className = "" }: { w?: string; className?: string }) {
  return <span className={`da-shimmer block h-3.5 rounded ${w} ${className}`} />;
}

/**
 * Un card de produs in curs de incarcare.
 *
 * Repeta structura reala din `OfferCard`: poza cu acelasi raport, doua randuri de
 * titlu, un rand de pret. De aceea grila nu se rearanjeaza cand sosesc datele.
 */
export function OfferCardSkeleton() {
  return (
    <li className="da-card overflow-hidden" aria-hidden="true">
      {/* `4/3` — exact raportul pozei reale din `OfferCard`. Orice alt raport
          reintroduce chiar saltul pe care scheletul exista sa il previna. */}
      <span className="da-shimmer block aspect-[4/3] w-full" />
      <span className="flex flex-col gap-2 p-3">
        <SkeletonLine w="w-4/5" />
        <SkeletonLine w="w-3/5" className="h-3" />
        <SkeletonLine w="w-1/3" className="mt-1 h-4" />
      </span>
    </li>
  );
}

/**
 * Grila de produse in asteptare.
 *
 * `count` e mic in mod deliberat: scheletele umplu primul ecran, nu tot meniul.
 * Randarea a sute de forme fantoma ar costa exact cat lista adevarata, adica ar
 * intarzia chiar afisarea pe care o acopera.
 */
export function OfferGridSkeleton({ count = 6 }: { count?: number }) {
  return (
    <ul className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-4">
      {Array.from({ length: count }, (_, index) => (
        <OfferCardSkeleton key={index} />
      ))}
    </ul>
  );
}

/** Antetul vitrinei: banda de poza, titlu, un rand de detalii. */
export function VendorHeaderSkeleton() {
  return (
    <div className="flex flex-col gap-4" aria-hidden="true">
      {/* Fara varianta `sm:`: antetul real e `16/6` la toate pragurile, iar un
          `21/6` pe desktop lasa ~100 px care se umplu brusc la sosirea datelor. */}
      <span className="da-shimmer block aspect-[16/6] w-full rounded-card" />
      <span className="flex flex-col gap-2">
        <SkeletonLine w="w-1/2" className="h-7" />
        <SkeletonLine w="w-2/3" />
      </span>
    </div>
  );
}
